import { useQuery } from '@tanstack/react-query';

import { api } from '@shared/api/client';
import { asRows } from '@shared/api/envelope';
import { key, STALE_TIME } from '@shared/api/query-keys';

import type { TagListPayload, TagTerm } from '../_shared/types';

/**
 * The flat tag vocabulary.
 *
 * As with the tree, the owning service publishes no `admin/tags` list — the
 * administration family carries the three writes and nothing to read. The
 * console reads the same route the public chip strip reads, which is correct
 * rather than a workaround: a tag has no state, no owner, and no operator-only
 * field, so a second projection would return the identical rows.
 *
 * **No page parameter is sent.** No catalogue row grants this surface a
 * traversal control, so it asks for the vocabulary and states what it received
 * against what exists.
 */
export function tagsKey() {
  return key('content', 'tags');
}

export function useTags() {
  return useQuery({
    queryKey: tagsKey(),
    // The vocabulary arrives as a bare array — it carries no pagination, so it
    // carries no `items` wrapper either. `total` is the length of what arrived,
    // which is what the truncation line can honestly state.
    queryFn: async (): Promise<TagListPayload> => {
      const items = asRows<TagTerm>(await api.get('content', '/tags'));
      return { items, total: items.length };
    },
    staleTime: STALE_TIME.vocabulary,
  });
}
